import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {ApiResponse} from "../../../classes/api-response";
import {UsersService} from "../../../services/users.service";
import {SnackService} from "../../../services/snack.service";
import {ActivatedRoute, Router} from "@angular/router";
import {User} from "../../../model/user";
import {merge} from "lodash-es";
import {Company} from "../../../model/company";
import {AuthService} from "../../../services/auth.service";
import {CompaniesService} from "../../../services/companies.service";
import { Permission } from 'src/app/model/permission';
import { PermissionsService } from 'src/app/services/permissions.service';

@Component({
  selector: 'aw-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss']
})
export class UserFormComponent implements OnInit {
  userForm: UntypedFormGroup;
  @Output() saved = new EventEmitter<void>();
  @Output() canceled = new EventEmitter<void>();
  saving = false;
  generalError?: ApiResponse;
  previousUrl: string = '';
  loadingCompanies = false;
  companies: Company[] = [];
  // selectedCompany: Company | undefined = new Company();
  loggedUser: User;
  isEditing = false;
  permissions: Permission[] = [];
  loadingPermissions = false;
  selectedPermissionNames: string[] = [];
  protected readonly console = console;


  constructor(
    private userService: UsersService,
    private snackService: SnackService,
    private router: Router,
    private route: ActivatedRoute,
    private companiesService: CompaniesService,
    private permissionsService: PermissionsService,
    private authService: AuthService,
  ) {
    if (route.snapshot.url[0])
      this.previousUrl = route.snapshot.url[0].path;
    this.userForm = new UntypedFormGroup(
      {
        email: new UntypedFormControl(
          '',
          [
            Validators.pattern("[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?"),
            Validators.required
          ]
        ),
        firstName: new UntypedFormControl(
          '',
          [
            Validators.required,
          ]
        ),
        lastName: new UntypedFormControl(
          '',
          [
            Validators.required,
          ]
        ),
        phone: new UntypedFormControl(
          '',
          [
            Validators.pattern('[- +()0-9]+'),
            Validators.required
          ]
        )
      },
    );
  }

  _user = new User();

  @Input() set user(value: User) {
    this._user = merge(new User(), value);
    this.isEditing = !!value?.id;


    if (this.isEditing) {
      this.userForm.controls.email.disable();
      if (this._user.company) {
        this.addCompanyField();
      }
    }

    this.userForm.patchValue(this._user);
  }

  ngOnInit(): void {
    this.loggedUser = this.authService.loggedUser();
    this.loadingCompanies = true;
    if (!this.loggedUser.permissions.find((permission)=> permission.name === 'admin')) {
      if (this.loggedUser.company?.id) {
        this.loadingCompanies = true;
        this.companiesService.getCompanyById(this.loggedUser.company.id).subscribe(company => {
          this.companies = [company];
          this.loadingCompanies = false;
          this.addCompanyField();
          this.userForm.get('company')?.setValue(company);
        });
      }
    } else {
      this.loadingCompanies = true;
      this.companiesService.getAllCompanies().subscribe(companies => {
        this.companies = companies;
        this.loadingCompanies = false;
      });
    }
    this.loadingPermissions = true;
    this.permissionsService.getAllPermissions().subscribe(perms => {
      this.permissions = perms;
      this.loadingPermissions = false;
    });
  }

  addCompanyField(): void {
    if(!this._user.company)
      this._user.company = {} as Company;

    this.userForm.addControl(
      'company',
      new UntypedFormControl('', Validators.required)
    );
  }

  
  addResearcherFields(): void {
    if (!this.userForm.contains('address')) {
      this.userForm.addControl('address', new UntypedFormControl('', Validators.required));
      this.userForm.addControl('macAddress', new UntypedFormControl('', [
        Validators.required,
        Validators.pattern('^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$')
      ]));
      this.userForm.addControl('operatingSystem', new UntypedFormControl('', Validators.required));
      this.userForm.addControl('organizationName', new UntypedFormControl('', Validators.required));
    }
  }

  removeResearcherFields(): void {
    ['address', 'macAddress', 'operatingSystem', 'organizationName'].forEach(field => {
      if (this.userForm.contains(field)) {
        this.userForm.removeControl(field);
      }
    });
  }

  saveUser(): void {
    this.saving = true;

    const newUser = merge(new User(), this._user, this.userForm.getRawValue());

    this.generalError = undefined;

    if (this.userForm.contains('company')) {
      this._user.company = this.userForm.get('company')?.value;
    }
    if (this._user.id) {
      this.userService.updateUser(newUser).subscribe(
        () => {
          this.saving = false;
          this.snackService.success('User updated!');
          this.saved.emit();
        },
        error => {
          this.saving = false;
          this.generalError = error;
          this.snackService.error(error.message);
        }
      );
    } else {
      this.userService.createUser(newUser).subscribe(
        (createdUser) => {
          if (this.selectedPermissionNames.length > 0) {
            this.userService.addUserPermissions(createdUser.id, this.selectedPermissionNames).subscribe(
              () => {
                this.saving = false;
                this.snackService.success('User created!');
                this.saved.emit();
              },
              error => {
                this.saving = false;
                this.snackService.error(error.message);
              }
            );
          } else {
            this.saving = false;
            this.snackService.success('User created!');
            this.saved.emit();
          }
        },
        error => {
          this.saving = false;
          this.generalError = error;
          this.snackService.error(error.message);
        }
      );
    }
  }

  removeCompanyField(): void {
    if (this.userForm.contains('company')) {
      this.userForm.removeControl('company');
      this._user.company = undefined;
    }
  }
  togglePermission(name: string): void {
    this.selectedPermissionNames = this.selectedPermissionNames.includes(name)
      ? this.selectedPermissionNames.filter(p => p !== name)
      : [...this.selectedPermissionNames, name];
  }
  
  onCancel(): void {
    if (this.previousUrl === 'edit')
      this.router.navigateByUrl("/reserved/profile").then();
    this.user = this._user;
    this.canceled.emit();
  }
  get isAdmin(): boolean {
    return !!this.loggedUser.permissions.find(p => p.name === 'admin');
  }
  isPermissionDisabled(permissionName: string): boolean {
    if (permissionName === 'admin') {
      return this.selectedPermissionNames.includes('customer');
    }
    if (permissionName === 'customer') {
      return this.selectedPermissionNames.includes('admin');
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  protected compareCompany: (c1: Company, c2: Company) => boolean = (c1: Company, c2: Company) => {
    console.log('compare',c1,c2);
    return c1.id == c2.id;
  };


 
}
